import React, { useState } from 'react';
import { Code, Book, Key, Zap, Shield, Terminal, Copy, Check } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Helmet } from 'react-helmet';
import { toast } from 'sonner';

const ApiDocs = () => {
  const [copiedEndpoint, setCopiedEndpoint] = useState(null);

  const copyToClipboard = (text, endpoint) => {
    navigator.clipboard.writeText(text);
    setCopiedEndpoint(endpoint);
    toast.success('Copied to clipboard!');
    setTimeout(() => setCopiedEndpoint(null), 2000);
  };

  const endpoints = [
    {
      method: 'POST',
      path: '/api/designs/generate',
      title: 'Generate Design',
      description: 'Create a new AI-generated T-shirt design',
      request: {
        prompt: 'A dragon breathing colorful flames',
        projectId: null,
        aspectRatio: 'ASPECT_1_1',
      },
      response: {
        success: true,
        projectId: 'abc123xyz',
        versionId: 'v1',
        imageUrl: 'https://storage.googleapis.com/...',
      },
    },
    {
      method: 'POST',
      path: '/api/designs/regenerate',
      title: 'Edit/Regenerate Design',
      description: 'Modify an existing design with new prompts',
      request: {
        projectId: 'abc123xyz',
        editPrompt: 'Make the flames blue instead of red',
      },
      response: {
        success: true,
        versionId: 'v2',
        imageUrl: 'https://storage.googleapis.com/...',
      },
    },
    {
      method: 'GET',
      path: '/api/projects',
      title: 'Get User Projects',
      description: 'Retrieve all projects for the authenticated user',
      request: null,
      response: {
        projects: [
          {
            id: 'abc123',
            name: 'Dragon Design',
            versions: 5,
            updatedAt: '2025-01-11T00:00:00Z',
          },
        ],
      },
    },
    {
      method: 'GET',
      path: '/api/user/profile',
      title: 'Get User Profile',
      description: 'Get authenticated user profile and credits',
      request: null,
      response: {
        email: 'user@example.com',
        credits: 3000,
        plan: 'Pro',
      },
    },
  ];

  return (
    <>
      <Helmet>
        <title>API Documentation - Fluxara</title>
        <meta name="description" content="Comprehensive API documentation for Fluxara design generation platform." />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-gray-900 to-gray-800 text-white">
        {/* Hero */}
        <section className="py-20 px-6 bg-gradient-to-br from-cyan-900 to-turquoise-900">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center gap-6 mb-8">
              <div className="w-20 h-20 bg-gradient-to-br from-cyan-400 to-turquoise-400 rounded-3xl flex items-center justify-center shadow-2xl">
                <Code className="w-10 h-10 text-white" />
              </div>
              <div>
                <h1 className="text-5xl md:text-6xl font-black">API Documentation</h1>
                <p className="text-xl text-cyan-200 mt-2">Integrate Fluxara's AI design generation into your apps</p>
              </div>
            </div>

            <div className="grid md:grid-cols-3 gap-6">
              <div className="glass-dark p-6 rounded-2xl">
                <Book className="w-8 h-8 text-cyan-400 mb-3" />
                <h3 className="text-xl font-bold mb-2">RESTful API</h3>
                <p className="text-gray-300">Simple HTTP endpoints for all operations</p>
              </div>
              <div className="glass-dark p-6 rounded-2xl">
                <Shield className="w-8 h-8 text-turquoise-400 mb-3" />
                <h3 className="text-xl font-bold mb-2">OAuth 2.0</h3>
                <p className="text-gray-300">Secure Firebase authentication</p>
              </div>
              <div className="glass-dark p-6 rounded-2xl">
                <Zap className="w-8 h-8 text-yellow-400 mb-3" />
                <h3 className="text-xl font-bold mb-2">Fast Response</h3>
                <p className="text-gray-300">Optimized for speed and reliability</p>
              </div>
            </div>
          </div>
        </section>

        {/* Authentication */}
        <section className="py-20 px-6">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center gap-4 mb-12">
              <Key className="w-10 h-10 text-cyan-400" />
              <h2 className="text-4xl font-black">Authentication</h2>
            </div>

            <Card className="glass-dark border-0 shadow-2xl">
              <CardContent className="p-8">
                <h3 className="text-2xl font-bold mb-4 text-cyan-400">Firebase Authentication</h3>
                <p className="text-gray-300 mb-6 leading-relaxed">
                  All API requests require Firebase authentication. Include the user's Firebase ID token in the Authorization header.
                </p>

                <div className="bg-gray-950 p-6 rounded-xl border border-cyan-500/30">
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-cyan-400 font-mono text-sm">Authentication Header</span>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => copyToClipboard('Authorization: Bearer <FIREBASE_ID_TOKEN>', 'auth')}
                    >
                      {copiedEndpoint === 'auth' ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                    </Button>
                  </div>
                  <code className="text-green-400 font-mono">
                    Authorization: Bearer &lt;FIREBASE_ID_TOKEN&gt;
                  </code>
                </div>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* Endpoints */}
        <section className="py-20 px-6 bg-gray-950/50">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center gap-4 mb-12">
              <Terminal className="w-10 h-10 text-cyan-400" />
              <h2 className="text-4xl font-black">API Endpoints</h2>
            </div>

            <div className="space-y-12">
              {endpoints.map((endpoint, index) => (
                <Card key={index} className="glass-dark border-cyan-500/20 shadow-2xl">
                  <CardContent className="p-8">
                    <div className="flex items-start justify-between mb-6">
                      <div>
                        <div className="flex items-center gap-4 mb-3">
                          <span className={`px-4 py-2 rounded-lg font-bold text-sm ${
                            endpoint.method === 'POST' ? 'bg-green-600' : 'bg-blue-600'
                          }`}>
                            {endpoint.method}
                          </span>
                          <code className="text-2xl font-mono text-cyan-400">{endpoint.path}</code>
                        </div>
                        <h3 className="text-2xl font-bold mb-2">{endpoint.title}</h3>
                        <p className="text-gray-400">{endpoint.description}</p>
                      </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-6">
                      {/* Request */}
                      {endpoint.request && (
                        <div>
                          <h4 className="text-lg font-bold text-cyan-400 mb-4">Request Body</h4>
                          <div className="bg-gray-950 p-6 rounded-xl border border-cyan-500/30 relative">
                            <Button
                              size="sm"
                              variant="ghost"
                              className="absolute top-2 right-2"
                              onClick={() => copyToClipboard(JSON.stringify(endpoint.request, null, 2), `request-${index}`)}
                            >
                              {copiedEndpoint === `request-${index}` ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                            </Button>
                            <pre className="text-green-400 font-mono text-sm overflow-x-auto">
                              {JSON.stringify(endpoint.request, null, 2)}
                            </pre>
                          </div>
                        </div>
                      )}

                      {/* Response */}
                      <div>
                        <h4 className="text-lg font-bold text-turquoise-400 mb-4">Response</h4>
                        <div className="bg-gray-950 p-6 rounded-xl border border-turquoise-500/30 relative">
                          <Button
                            size="sm"
                            variant="ghost"
                            className="absolute top-2 right-2"
                            onClick={() => copyToClipboard(JSON.stringify(endpoint.response, null, 2), `response-${index}`)}
                          >
                            {copiedEndpoint === `response-${index}` ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                          </Button>
                          <pre className="text-blue-400 font-mono text-sm overflow-x-auto">
                            {JSON.stringify(endpoint.response, null, 2)}
                          </pre>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Rate Limits */}
        <section className="py-20 px-6">
          <div className="max-w-7xl mx-auto">
            <h2 className="text-4xl font-black mb-12">Rate Limits & Quotas</h2>
            
            <div className="grid md:grid-cols-2 gap-6">
              <Card className="glass-dark border-0 shadow-xl">
                <CardContent className="p-8">
                  <h3 className="text-2xl font-bold text-cyan-400 mb-4">Credit System</h3>
                  <div className="space-y-3 text-gray-300">
                    <p><strong>New Design:</strong> 36 credits</p>
                    <p><strong>Edit/Regenerate:</strong> 19 credits</p>
                    <p><strong>Version History:</strong> Free</p>
                  </div>
                </CardContent>
              </Card>

              <Card className="glass-dark border-0 shadow-xl">
                <CardContent className="p-8">
                  <h3 className="text-2xl font-bold text-turquoise-400 mb-4">API Limits</h3>
                  <div className="space-y-3 text-gray-300">
                    <p><strong>Rate Limit:</strong> 60 requests/minute</p>
                    <p><strong>Max File Size:</strong> 10MB</p>
                    <p><strong>Timeout:</strong> 120 seconds</p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-20 px-6 bg-gradient-to-br from-cyan-600 to-turquoise-600">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl font-black mb-6">Ready to Build?</h2>
            <p className="text-xl text-cyan-100 mb-8">
              Start integrating Fluxara API into your application today
            </p>
            <div className="flex gap-4 justify-center">
              <Button
                size="lg"
                className="text-xl px-12 py-8 bg-white text-cyan-900 hover:bg-gray-100 shadow-2xl font-bold"
              >
                <Key className="mr-3 w-6 h-6" />
                Get API Key
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="text-xl px-12 py-8 border-2 border-white text-white hover:bg-white/20"
              >
                <Book className="mr-3 w-6 h-6" />
                View Examples
              </Button>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default ApiDocs;
